import {
  FormatBold,
  FormatClear,
  FormatItalic,
  FormatListBulleted,
  FormatListNumbered,
  FormatUnderlined,
  Link as LinkIcon,
  Title,
} from "@mui/icons-material";
import { Box, Divider, FormLabel, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import Link from "@tiptap/extension-link";
import Placeholder from "@tiptap/extension-placeholder";
import Underline from "@tiptap/extension-underline";
import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { type ReactNode, useEffect } from "react";

import { plainTextToEditorHtml } from "./salesEmailHtml";

export interface SalesEmailBodyEditorProps {
  label?: string;
  minRows?: number;
  onChange: (html: string) => void;
  placeholder?: string;
  value: string;
}

interface ToolbarButtonProps {
  active?: boolean;
  disabled?: boolean;
  label: string;
  onClick: () => void;
  children: ReactNode;
}

const ToolbarButton = ({ active, children, disabled, label, onClick }: ToolbarButtonProps) => (
  <Tooltip title={label}>
    <span>
      <IconButton
        aria-label={label}
        color={active ? "primary" : "default"}
        disabled={disabled}
        onClick={onClick}
        size="small"
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

export const SalesEmailBodyEditor = ({
  label = "Body",
  minRows = 8,
  onChange,
  placeholder = "Write your email…",
  value,
}: SalesEmailBodyEditorProps) => {
  const editor = useEditor({
    content: plainTextToEditorHtml(value),
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Underline,
      Link.configure({
        HTMLAttributes: {
          rel: "noopener noreferrer",
          target: "_blank",
        },
        openOnClick: false,
      }),
      Placeholder.configure({ placeholder }),
    ],
    immediatelyRender: false,
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
  });

  // Sync when parent replaces value (template selection, dialog open).
  useEffect(() => {
    if (!editor) {
      return;
    }
    const next = plainTextToEditorHtml(value);
    if (editor.getHTML() === next) {
      return;
    }
    // Avoid clobbering while the user is typing the same document.
    const currentText = editor.getText().trim();
    const nextText = editor.view.dom.ownerDocument.createElement("div");
    nextText.innerHTML = next;
    if (currentText === (nextText.textContent ?? "").trim() && editor.isFocused) {
      return;
    }
    editor.commands.setContent(next, { emitUpdate: false });
  }, [editor, value]);

  if (!editor) {
    return null;
  }

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link URL", previous ?? "https://");
    if (url === null) {
      return;
    }
    if (url.trim() === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url.trim() }).run();
  };

  const minHeight = Math.max(minRows, 4) * 24;

  return (
    <Box>
      {label ? (
        <FormLabel sx={{ display: "block", mb: 0.75 }}>
          <Typography component="span" variant="body2">
            {label}
          </Typography>
        </FormLabel>
      ) : null}
      <Box
        sx={{
          "& .ProseMirror": {
            "& a": { color: "primary.main" },
            "& h2": { fontSize: "1.25rem", fontWeight: 700, m: 0, mb: 1 },
            "& h3": { fontSize: "1.1rem", fontWeight: 600, m: 0, mb: 1 },
            "& p": { m: 0, mb: 1 },
            "& p.is-editor-empty:first-of-type::before": {
              color: "text.disabled",
              content: "attr(data-placeholder)",
              float: "left",
              height: 0,
              pointerEvents: "none",
            },
            "& p:last-child": { mb: 0 },
            "& ul, & ol": { m: 0, mb: 1, pl: 2.5 },
            "&.ProseMirror-focused": { outline: "none" },
            minHeight,
            outline: "none",
            px: 1.5,
            py: 1.25,
          },
          "&:focus-within": {
            borderColor: "primary.main",
            boxShadow: (theme) => `0 0 0 1px ${theme.palette.primary.main}`,
          },
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
          overflow: "hidden",
        }}
      >
        <Stack
          alignItems="center"
          direction="row"
          flexWrap="wrap"
          spacing={0.25}
          sx={{ bgcolor: "action.hover", borderBottom: 1, borderColor: "divider", px: 0.5, py: 0.25 }}
        >
          <ToolbarButton
            active={editor.isActive("bold")}
            label="Bold"
            onClick={() => editor.chain().focus().toggleBold().run()}
          >
            <FormatBold fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("italic")}
            label="Italic"
            onClick={() => editor.chain().focus().toggleItalic().run()}
          >
            <FormatItalic fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("underline")}
            label="Underline"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
          >
            <FormatUnderlined fontSize="small" />
          </ToolbarButton>
          <Divider flexItem orientation="vertical" sx={{ mx: 0.5, my: 0.75 }} />
          <ToolbarButton
            active={editor.isActive("heading", { level: 2 })}
            label="Heading"
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
          >
            <Title fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("bulletList")}
            label="Bulleted list"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          >
            <FormatListBulleted fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            active={editor.isActive("orderedList")}
            label="Numbered list"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          >
            <FormatListNumbered fontSize="small" />
          </ToolbarButton>
          <ToolbarButton active={editor.isActive("link")} label="Link" onClick={setLink}>
            <LinkIcon fontSize="small" />
          </ToolbarButton>
          <ToolbarButton
            label="Clear formatting"
            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
          >
            <FormatClear fontSize="small" />
          </ToolbarButton>
        </Stack>
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
};
